import { FC, Fragment, PropsWithChildren, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { classNames } from 'utils';
import Loader from './loader';
import { useApp } from 'hooks';

interface DrawerProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (val: boolean) => void;
  loading?: boolean;
  size?: 'md' | 'lg' | 'xl';
}

const sizeMap = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
  open,
  setOpen,
  children,
  title,
  description,
  loading,
  size = 'xl',
}) => {
  const closeButtonRef = useRef(null);
  const { theme } = useApp();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className={classNames(theme, 'fixed inset-0 z-50 overflow-hidden')}
        initialFocus={closeButtonRef}
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div
                className={classNames(
                  sizeMap[size],
                  'pointer-events-auto w-screen'
                )}
              >
                <div className="flex h-full flex-col bg-white shadow-xl">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          {title}
                        </Dialog.Title>
                        {description ? (
                          <p className="mt-1 text-sm text-gray-500">
                            {description}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        ref={closeButtonRef}
                        className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={() => setOpen(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                      <div className="flex min-h-[200px] items-center justify-center">
                        <Loader />
                      </div>
                    ) : (
                      children
                    )}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Drawer;
